import {
  catalogBySlug,
  detectOutliers,
  epley,
  isE1rmRepCount,
  toCanonicalKg,
  type Exercise,
  type PRsByExercise,
} from '@jackedlogs/shared';
import { parseLocaleNumber } from './parseLocaleNumber';
import type { LoggerDraft } from './state/loggerDraftTypes';

// Outlier weight detection at save time. Mirrors web's detectOutliers
// in WorkoutLog.tsx (shipped 0.83.0): for every weighted set in the
// draft, compare the user-typed weight against the historical
// per-exercise max. Threshold `weight > max × 3` flags the typical
// typo case (54.5 kg → 504 kg, a 10× decimal shift). The source of
// truth for max is the same /api/me/records aggregate that drives PR
// detection — the 1RM bucket holds the all-time heaviest load, by
// the invariant that any heavier set fills 1..reps buckets. First-
// ever-on-this-exercise lifts get no warning (max=0 → skipped),
// matching web behaviour. Bodyweight entries skip entirely: assistance
// / vest values are small and noisy, and history rarely settles into
// a stable comparator.
//
// Split out of workoutPayload.ts (AP-0p, LG-8) so both files stay under
// the 500-line cap; it's a self-contained pure concern (no coupling to
// the draft→payload builders it used to sit beside).
//
// Project the mobile draft into the shared `detectOutliers` input
// shape. Catalog gives us per-entry equipment for the no-history
// ceiling fallback. The history map is built from the prHistory
// aggregate (per-exercise rep-max bucket 1 = current 1RM weight) so
// the historical comparison stays as-was.
export const detectOutliersForDraft = (
  draft: LoggerDraft,
  prHistory: PRsByExercise | null,
  catalog: ReadonlyArray<Exercise> | null,
) => {
  const recentMaxKgBySlug = new Map<string, number>();
  // Best e1RM per slug for the e1rm-jump check. The 1RM bucket weight
  // alone isn't the best e1RM (a moderate-weight high-rep set can imply a
  // higher 1RM), so walk every PR record and take the max epley gated by
  // the rep cap. Records repeat across buckets — Math.max is idempotent.
  const recentE1rmKgBySlug = new Map<string, number>();
  if (prHistory) {
    for (const [slug, buckets] of Object.entries(prHistory)) {
      const max = buckets?.[1]?.weightKg ?? 0;
      if (max > 0) recentMaxKgBySlug.set(slug, max);
      let bestE1rm = 0;
      for (const rec of Object.values(buckets ?? {})) {
        if (!rec || !isE1rmRepCount(rec.reps)) continue;
        if (rec.weightKg <= 0) continue;
        bestE1rm = Math.max(bestE1rm, epley(rec.weightKg, rec.reps));
      }
      if (bestE1rm > 0) recentE1rmKgBySlug.set(slug, bestE1rm);
    }
  }
  const bySlug = catalogBySlug(catalog ?? []);
  const entries = draft.entries.map((entry) => {
    const ex = bySlug.get(entry.exerciseId);
    return {
      exerciseId: entry.exerciseId,
      exerciseName: entry.exerciseName,
      isBodyweight: entry.isBodyweight,
      isCardio: entry.isCardio,
      equipment: ex?.equipment,
      // Entry laterality feeds the per-implement-double check: ~2× the
      // recent best on a ×2-counted lift = "entered the total of both
      // implements" signature (IB-8 Slice 2).
      loadedSides: entry.loadedSides,
      sets: entry.sets.map((s) => {
        const num = parseLocaleNumber(s.weight);
        const weightKg =
          Number.isFinite(num) && num > 0 ? toCanonicalKg(num, s.unit) : null;
        const repsNum = parseLocaleNumber(s.reps);
        const reps = Number.isFinite(repsNum) ? repsNum : null;
        return { weightKg, reps, kind: 'weighted' as const };
      }),
    };
  });
  return detectOutliers({ entries, recentMaxKgBySlug, recentE1rmKgBySlug });
};
